import React, { useState, useEffect, useMemo, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, ArrowLeft, LayoutDashboard } from 'lucide-react';
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../firebase';
import ArtHistoryAppreciation from './ArtHistoryAppreciation';
import ChooseArtworkModal, {
  getKeyFromTypeOfArtwork,
  getArtworkPickerPreviewUrl,
  getArtworkFullImageCandidates,
} from '../components/ChooseArtworkModal';
import { backendConfig, API_ENDPOINTS } from '../config/backendConfig';
import { resolveImageUrl, resolveImageUrlAsync } from '../utils/urlResolver';
import { enqueueAndPoll } from '../utils/jobPolling';
import { useAuth } from '../context/AuthContext';
import {
  getFlowForArtwork,
  saveFlowStep1,
  mergeFlowStep1Data,
  saveFlowStep2,
  saveFlowStep3,
  markFlowUploaded,
} from '../utils/artHistoryFlowService';
import { createCacheKey, clearCache } from '../utils/cache';
import { getCachedFlowActivity, setCachedFlowActivity } from '../utils/flowActivityCache';
import ArtworkFillDisplay from '../components/ArtworkFillDisplay';

const FLOW_SHELL_CLASS =
  'w-full max-w-6xl rounded-[28px] flex flex-col flex-1 min-h-0 overflow-hidden border border-[#e8d4bc]/90 bg-gradient-to-b from-[#fffdf9] to-[#fff6ec] shadow-[0_24px_80px_rgba(120,72,20,0.14)]';

const flowStepPillClass = (active, done, locked) => {
  if (locked) {
    return 'cursor-default border border-[#efe2d0] bg-[#faf6f0] text-[#8A7357]/75';
  }
  if (active) {
    return 'border border-[#f5d99b] bg-gradient-to-r from-[#fde68a] to-[#fbbf24] text-[#2F251B] shadow-sm';
  }
  if (done) {
    return 'border border-[#e8d4bc] bg-white text-[#2F251B] hover:border-[#f5d99b] hover:bg-[#fff9ef]';
  }
  return 'border border-[#e8d4bc] bg-white/80 text-[#5e5a54] hover:border-[#f5d99b] hover:bg-white';
};

/** Build a stable key for flow-activity grouping (same activity = same key). */
function buildFlowActivityKey(primary, comparison, activityTitle) {
  const a = (primary?.id || primary?.imageUrl || '').toString();
  const b = (comparison?.id || comparison?.imageUrl || '').toString();
  const t = (activityTitle || '').toString();
  return `flow-${a}-${b}-${t}`.replace(/\s+/g, '-').slice(0, 150);
}

const STEP_LABELS = ['Art History Appreciation', 'Compare Artworks', 'Try an Activity'];

/** Small WebP tile only — shows immediately while full image resolves. */
function previewHeroForArtwork(artwork) {
  if (!artwork?.id) return '';
  const raw = getArtworkPickerPreviewUrl(artwork.id);
  return raw ? resolveImageUrl(raw) : '';
}

/** Full-resolution hero (after selection) — no picker preview in this chain. */
function computeFullHeroSrcSync(artwork) {
  if (!artwork || typeof artwork !== 'object') return '';
  const rawList = [];
  const push = (u) => {
    const s = String(u || '').trim();
    if (s && !rawList.includes(s)) rawList.push(s);
  };
  try {
    getArtworkFullImageCandidates(artwork).forEach((u) => push(u));
  } catch (_) {}
  push(artwork.verifiedImageUrl);
  push(artwork.imageUrl);
  push(artwork.originalImageUrl);
  push(artwork.coverUrl);
  const httpsFirst = rawList.filter((x) => /^https?:\/\//i.test(String(x).trim()));
  const rest = rawList.filter((x) => !/^https?:\/\//i.test(String(x).trim()));
  const ordered = [...httpsFirst, ...rest];
  for (const raw of ordered) {
    const r = resolveImageUrl(raw);
    if (r && typeof r === 'string' && r.length > 0) return r;
  }
  return '';
}

/**
 * Learn Art History flow shell: manages state across 3 steps.
 * State from location: primaryArtwork, artworkTypeKey, level, artworkTypeLabel.
 * Step 1: Art History Appreciation (embedded). Step 2 & 3 TBD.
 */
const API_BASE = backendConfig.baseUrl;

const LearnArtHistoryFlow = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};
  const { user } = useAuth();

  const primaryArtwork = state.primaryArtwork || state.artwork;
  const previewHeroSrc = useMemo(
    () => previewHeroForArtwork(primaryArtwork),
    [primaryArtwork]
  );
  const fullHeroSrcSync = useMemo(
    () => computeFullHeroSrcSync(primaryArtwork),
    [primaryArtwork]
  );
  const [primaryHeroSrcAsync, setPrimaryHeroSrcAsync] = useState('');
  /** Best full-res URL (after selection); preview tile loads first for instant paint. */
  const fullHeroSrc = fullHeroSrcSync || primaryHeroSrcAsync;
  /** When both preview + full exist, hide preview only after full bitmap decodes. */
  const [fullHeroDecoded, setFullHeroDecoded] = useState(false);

  useLayoutEffect(() => {
    setPrimaryHeroSrcAsync('');
    setFullHeroDecoded(false);
  }, [primaryArtwork?.id]);

  useEffect(() => {
    setFullHeroDecoded(false);
  }, [fullHeroSrc]);

  // Storage paths / gs:// need async resolution when full-res sync chain did not yield a URL.
  useEffect(() => {
    if (!primaryArtwork || fullHeroSrcSync) return;
    let cancelled = false;
    const raws = [
      primaryArtwork.verifiedImageStoragePath,
      primaryArtwork.imageUrl,
      primaryArtwork.verifiedImageUrl,
      primaryArtwork.originalImageUrl,
      primaryArtwork.coverUrl,
    ]
      .map((x) => (x != null ? String(x).trim() : ''))
      .filter(Boolean);
    (async () => {
      for (const raw of raws) {
        try {
          const resolved = await resolveImageUrlAsync(raw);
          if (resolved && typeof resolved === 'string' && resolved.length > 0 && !cancelled) {
            setPrimaryHeroSrcAsync(resolved);
            return;
          }
        } catch (_) {}
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [primaryArtwork, fullHeroSrcSync]);

  // Preload canonical preview tile (same as picker) for instant Step 1 hero.
  useEffect(() => {
    const href = previewHeroSrc;
    if (!href) return undefined;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = href;
    link.setAttribute('fetchpriority', 'high');
    document.head.appendChild(link);
    return () => {
      try {
        document.head.removeChild(link);
      } catch (_) {}
    };
  }, [primaryArtwork?.id, previewHeroSrc]);
  const artworkTypeKey = state.artworkTypeKey;
  const level = state.level;
  const artworkTypeLabel = state.artworkTypeLabel || artworkTypeKey;
  const resumeFlowId = state.flowId ?? null;

  // When resuming an old flow, card may not have artworkTypeKey/level; derive from primary artwork in artHistoryArtworks
  const [derivedCategory, setDerivedCategory] = useState(null);
  useEffect(() => {
    if (!primaryArtwork || (artworkTypeKey != null && artworkTypeKey !== '')) {
      setDerivedCategory(null);
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        if (primaryArtwork.id) {
          const ref = doc(db, 'artHistoryArtworks', primaryArtwork.id);
          const snap = await getDoc(ref);
          if (cancelled) return;
          if (snap.exists()) {
            const d = snap.data();
            const typeOfArtwork = d.typeOfArtwork || d.type || '';
            const levelType = d.levelType || '';
            const key = getKeyFromTypeOfArtwork(typeOfArtwork);
            const levelNum = levelType.replace(/level\s*/i, '').trim();
            const num = levelNum ? parseInt(levelNum, 10) : 1;
            if (key) setDerivedCategory({ artworkTypeKey: key, level: isNaN(num) ? 1 : num, artworkTypeLabel: typeOfArtwork || key });
            return;
          }
        }
        if (primaryArtwork.imageUrl) {
          const q = query(
            collection(db, 'artHistoryArtworks'),
            where('imageUrl', '==', primaryArtwork.imageUrl),
            limit(1)
          );
          const snapshot = await getDocs(q);
          if (cancelled) return;
          if (!snapshot.empty) {
            const d = snapshot.docs[0].data();
            const typeOfArtwork = d.typeOfArtwork || d.type || '';
            const levelType = d.levelType || '';
            const key = getKeyFromTypeOfArtwork(typeOfArtwork);
            const levelNum = levelType.replace(/level\s*/i, '').trim();
            const num = levelNum ? parseInt(levelNum, 10) : 1;
            if (key) setDerivedCategory({ artworkTypeKey: key, level: isNaN(num) ? 1 : num, artworkTypeLabel: typeOfArtwork || key });
          }
        }
      } catch (_) {
        if (!cancelled) setDerivedCategory(null);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [primaryArtwork?.id, primaryArtwork?.imageUrl, artworkTypeKey]);

  const effectiveArtworkTypeKey = artworkTypeKey ?? derivedCategory?.artworkTypeKey;
  const effectiveLevel = level ?? derivedCategory?.level ?? 1;
  const effectiveArtworkTypeLabel = artworkTypeLabel || derivedCategory?.artworkTypeLabel || effectiveArtworkTypeKey;

  const [stepIndex, setStepIndex] = useState(0);
  const [step1Complete, setStep1Complete] = useState(false);
  const [step1AnalysisComplete, setStep1AnalysisComplete] = useState(false);
  const [step1AppreciationResult, setStep1AppreciationResult] = useState(null);
  const [step1SaveError, setStep1SaveError] = useState(null);
  const [step1Saving, setStep1Saving] = useState(false);
  const [currentFlowId, setCurrentFlowId] = useState(resumeFlowId);

  // Step 2: Compare artworks
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [comparisonArtwork, setComparisonArtwork] = useState(null);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [isComparing, setIsComparing] = useState(false);
  const [comparisonError, setComparisonError] = useState(null);
  const [visibleSection, setVisibleSection] = useState(null);
  const [expandedPoint, setExpandedPoint] = useState(null);
  const [step2Complete, setStep2Complete] = useState(false);

  // Step 3: Try an Activity (generated from comparison)
  const [step3Activity, setStep3Activity] = useState(null);
  const [step3Loading, setStep3Loading] = useState(false);
  const [step3Error, setStep3Error] = useState(null);
  const [expandedActivitySteps, setExpandedActivitySteps] = useState({});
  const [step3SectionsOpen, setStep3SectionsOpen] = useState({
    materials: true,
    before: true,
    steps: true,
    techniqueMix: true,
    keyTips: true,
    bonus: true,
    reflection: true,
  });

  // Step 3: Artwork upload and share to co-learning space
  const [activityFile, setActivityFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccessData, setUploadSuccessData] = useState(null);
  const [showShareConfirmation, setShowShareConfirmation] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showLetsGoModal, setShowLetsGoModal] = useState(false);
  const [letsGoModalStep, setLetsGoModalStep] = useState(1); // 1 = after Step 1, 2 = after Step 2

  useEffect(() => {
    if (!primaryArtwork && !state.artwork) {
      navigate('/learn-art-history', { replace: true });
    }
  }, [primaryArtwork, state.artwork, navigate]);

  // Load saved flow when resuming from My Art History.
  // If flowId is missing, fall back to latest flow for this artwork.
  const [flowLoaded, setFlowLoaded] = useState(false);
  useEffect(() => {
    if (!user?.uid || !primaryArtwork) {
      setFlowLoaded(true);
      return;
    }
    const shouldTryResume = !!resumeFlowId || state.fromMyArtHistory === true;
    if (!shouldTryResume) {
      setFlowLoaded(true);
      return;
    }
    let cancelled = false;
    getFlowForArtwork(user.uid, primaryArtwork, resumeFlowId || null)
      .then((flow) => {
        if (cancelled || !flow) {
          setFlowLoaded(true);
          return;
        }
        setCurrentFlowId(flow.id);
        const saved = flow.step1Data?.appreciationResult;
        const hasSavedStep1Data = !!(
          saved &&
          (
            (Array.isArray(saved.appreciation) && saved.appreciation.length > 0) ||
            (Array.isArray(saved.points) && saved.points.length > 0) ||
            (typeof saved.appreciation === 'string' && saved.appreciation.trim().length > 0)
          )
        );
        if (flow.step1Complete) setStep1Complete(true);
        if (flow.step1Complete || hasSavedStep1Data) setStep1AnalysisComplete(true);
        if (saved && (saved.appreciation || saved.points)) {
          setStep1AppreciationResult(saved);
        }
        if (flow.step2Complete && flow.step2Data) {
          setComparisonArtwork(flow.step2Data.comparisonArtwork || null);
          setComparisonResult(flow.step2Data.comparisonResult || null);
          setStep2Complete(true);
        }
        if (flow.step3Complete && flow.step3Data?.activity) {
          setStep3Activity(flow.step3Data.activity);
          if (flow.step2Data?.comparisonArtwork) setComparisonArtwork(flow.step2Data.comparisonArtwork);
          if (flow.step2Data?.comparisonResult) setComparisonResult(flow.step2Data.comparisonResult);
        }
        // Resume entry should always open on Step 1 (saved analysis), not jump to Step 2/3.
        if (state.fromMyArtHistory === true) {
          setStepIndex(0);
        } else if (flow.step3Complete) setStepIndex(2);
        else if (flow.step2Complete) setStepIndex(1);
        else if (flow.step1Complete || hasSavedStep1Data) setStepIndex(0);
        setFlowLoaded(true);
      })
      .catch(() => setFlowLoaded(true));
    return () => { cancelled = true; };
  }, [user?.uid, primaryArtwork, resumeFlowId, state.fromMyArtHistory]);

  // Persist Step 1 analysis to the flow doc so returning from Activity Dashboard / My Art History loads instantly (no cache round-trip).
  useEffect(() => {
    if (!user?.uid || !primaryArtwork || !currentFlowId || !step1AppreciationResult) return;
    const r = step1AppreciationResult;
    const hasContent =
      (Array.isArray(r.appreciation) && r.appreciation.length > 0) ||
      (Array.isArray(r.points) && r.points.length > 0) ||
      (typeof r.appreciation === 'string' && r.appreciation.length > 0);
    if (!hasContent) return;
    const t = setTimeout(() => {
      mergeFlowStep1Data(user.uid, primaryArtwork, currentFlowId, {
        appreciationResult: step1AppreciationResult,
      }).catch((err) => console.warn('[LearnArtHistoryFlow] mergeFlowStep1Data failed:', err));
    }, 600);
    return () => clearTimeout(t);
  }, [user?.uid, primaryArtwork, currentFlowId, step1AppreciationResult]);

  // When navigating to Step 2 with comparison already done, show Similarities by default so content is visible (no blank area)
  useEffect(() => {
    if (stepIndex === 1 && comparisonResult && visibleSection === null) {
      setVisibleSection('similarities');
    }
  }, [stepIndex, !!comparisonResult]);

  // Step 3: Start activity generation from "Let's Go" — use cached activity if same artwork pair, else generate and cache
  const handleStartStep3 = async () => {
    if (!comparisonResult || !primaryArtwork || !comparisonArtwork || step3Loading) return;
    setStep3Loading(true);
    setStep3Error(null);

    // Check Firebase cache for this artwork pair (order-independent)
    const cached = await getCachedFlowActivity(primaryArtwork, comparisonArtwork);
    if (cached) {
      setStep3Activity(cached);
      setStepIndex(2);
      setStep3Loading(false);
      if (user?.uid && primaryArtwork && comparisonArtwork && currentFlowId) {
        saveFlowStep3(user.uid, primaryArtwork, currentFlowId, comparisonArtwork, cached).catch(() => {});
      }
      return;
    }

    const activityEndpoint = API_BASE.endsWith('/api')
      ? `${API_BASE}/compare/generate-flow-activity`
      : `${API_BASE}/api/compare/generate-flow-activity`;

    enqueueAndPoll(
      activityEndpoint,
      {
        artwork1: primaryArtwork,
        artwork2: comparisonArtwork,
        similarities: comparisonResult.similarities || [],
        differences: comparisonResult.differences || [],
        insight: comparisonResult.insight || '',
      },
      { interval: 2000, maxAttempts: 60 }
    )
      .then((raw) => {
        // Normalize: backend returns activity; may be wrapped as { result } from job-status
        const result = raw?.result && (raw.result.title || raw.result.steps)
          ? raw.result
          : raw;
        const activity = result && (result.title || result.steps)
          ? {
              title: result.title || '',
              objective: result.objective || '',
              materials: Array.isArray(result.materials) ? result.materials : [],
              steps: Array.isArray(result.steps) ? result.steps : [],
            }
          : null;
        if (activity) {
          setCachedFlowActivity(primaryArtwork, comparisonArtwork, activity).catch(() => {});
          setStep3Activity(activity);
          setStep3Loading(false);
          if (user?.uid && primaryArtwork && comparisonArtwork && currentFlowId) {
            saveFlowStep3(user.uid, primaryArtwork, currentFlowId, comparisonArtwork, activity).catch(() => {});
          }
          requestAnimationFrame(() => setStepIndex(2));
        } else {
          setStep3Error('Activity could not be loaded. Please try again.');
          setStep3Loading(false);
        }
      })
      .catch((err) => {
        const errMsg = String(err?.message || '');

        // Local backend may not expose /compare/generate-flow-activity.
        // Build a deterministic fallback activity from comparison data so Step 3 can still continue.
        if (errMsg.toLowerCase().includes('not found') || errMsg.includes('404')) {
          const fallbackActivity = {
            title: `Create Your Own Art Blend: ${primaryArtwork?.title || 'Artwork 1'} + ${comparisonArtwork?.title || 'Artwork 2'}`,
            objective: 'Mix ideas from both artworks to create something new in your own style.',
            materials: [
              'Drawing paper or sketchbook',
              'Pencil and eraser',
              'Color tools (crayons, markers, or paints)',
              'A dark color and a light color'
            ],
            beforeYouStart: [
              'Look at both artworks for 1 minute each.',
              'Pick one idea from each artwork you want to combine.'
            ],
            steps: [
              `Sketch the main subject inspired by "${primaryArtwork?.title || 'the first artwork'}".`,
              `Add one style element inspired by "${comparisonArtwork?.title || 'the second artwork'}".`,
              'Use light and shadow to make important parts stand out.',
              'Add background details that connect both ideas.',
              'Refine outlines and add final color touches.'
            ],
            techniqueMix: [
              `Similarity to try: ${(comparisonResult?.similarities?.[0] || 'Use a shared visual idea from both artworks.').toString()}`,
              `Difference to explore: ${(comparisonResult?.differences?.[0] || 'Use a contrasting choice from each artwork.').toString()}`
            ],
            keyTips: [
              'Keep shapes simple first, then add detail.',
              'Use contrast (light vs dark) to guide the eye.',
              'Try one bold decision from each artwork.'
            ],
            bonusChallenge: 'Create a second version with a different color mood.',
            tinyReflection: [
              'Which artwork influenced your piece more, and why?',
              'What new technique did you try today?'
            ]
          };

          setStep3Activity(fallbackActivity);
          setStep3Loading(false);
          if (user?.uid && primaryArtwork && comparisonArtwork && currentFlowId) {
            saveFlowStep3(user.uid, primaryArtwork, currentFlowId, comparisonArtwork, fallbackActivity).catch(() => {});
          }
          requestAnimationFrame(() => setStepIndex(2));
          return;
        }

        setStep3Error(err?.message || 'Failed to generate activity. Please try again.');
        setStep3Loading(false);
      });
  };

  if (!primaryArtwork && !state.artwork) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(180deg, #FFF8E1 0%, #FFF3C0 100%)' }}
      >
        <div className="text-gray-600">Taking you back...</div>
      </div>
    );
  }

  const fromMyArtHistory = state.fromMyArtHistory === true;
  const fromLearnArtHistory = state.fromLearnArtHistory === true;
  const isResumeEntry = fromMyArtHistory || !!resumeFlowId;

  const handleBack = () => {
    if (stepIndex > 0) {
      setStepIndex((i) => i - 1);
    } else {
      if (fromMyArtHistory) {
        navigate('/my-art-history');
      } else if (fromLearnArtHistory) {
        navigate('/learn-art-history', {
          state: { restoreLevel: typeof effectiveLevel === 'number' ? effectiveLevel : Number(effectiveLevel) || undefined },
        });
      } else {
        navigate('/activity-dashboard');
      }
    }
  };

  const handleStep1Complete = async () => {
    setStep1SaveError(null);
    if (!user?.uid || !primaryArtwork) {
      setStep1Complete(true);
      setLetsGoModalStep(1);
      setShowLetsGoModal(true);
      return;
    }
    if (currentFlowId) {
      if (step1AppreciationResult) {
        try {
          await mergeFlowStep1Data(user.uid, primaryArtwork, currentFlowId, {
            appreciationResult: step1AppreciationResult,
          });
        } catch (e) {
          console.warn('[LearnArtHistoryFlow] mergeFlowStep1Data on Mark as Done failed:', e);
        }
      }
      setStep1Complete(true);
      setLetsGoModalStep(1);
      setShowLetsGoModal(true);
      return;
    }
    setStep1Saving(true);
    try {
      const step1Snapshot = step1AppreciationResult
        ? { appreciationResult: step1AppreciationResult }
        : {};
      const flowId = await saveFlowStep1(user.uid, primaryArtwork, step1Snapshot, {
        artworkTypeKey: effectiveArtworkTypeKey ?? undefined,
        level: effectiveLevel ?? undefined,
        artworkTypeLabel: effectiveArtworkTypeLabel || undefined,
      });
      if (flowId) setCurrentFlowId(flowId);
      setStep1Complete(true);
      setLetsGoModalStep(1);
      setShowLetsGoModal(true);
    } catch (err) {
      console.error('[LearnArtHistoryFlow] saveFlowStep1 failed:', err);
      setStep1SaveError(err?.message || 'Could not save progress. Try again.');
    } finally {
      setStep1Saving(false);
    }
  };

  const handleGoToStep2 = () => {
    setShowLetsGoModal(false);
    setStepIndex(1);
  };

  const handleCompareArtworkSelect = (artwork) => {
    setComparisonArtwork(artwork);
    setShowCompareModal(false);
    setComparisonResult(null);
    setComparisonError(null);
    setVisibleSection(null);
    setExpandedPoint(null);
  };

  const getImageUrl = (a) =>
    a?.imageUrl || a?.originalImageUrl || a?.coverUrl || a?.image || a?.images?.[0]?.url || a?.image_url || '';
  const normalizeArtwork = (x) =>
    x && {
      imageUrl: getImageUrl(x),
      title: x.title || 'Untitled',
      artistName: x.artistName || x.artist || 'Unknown',
      artMovement: x.artMovement || x.movement || 'Unknown',
      description: x.description || '',
    };

  const handleCompare = async () => {
    if (!primaryArtwork || !comparisonArtwork) return;
    const a1 = normalizeArtwork(primaryArtwork);
    const a2 = normalizeArtwork(comparisonArtwork);
    if (!a1?.imageUrl || !a2?.imageUrl) {
      setComparisonError('Both artworks need an image to compare.');
      return;
    }
    setIsComparing(true);
    setComparisonError(null);
    setComparisonResult(null);
    setVisibleSection(null);
    setExpandedPoint(null);
    try {
      const compareEndpoint = API_BASE.endsWith('/api')
        ? `${API_BASE}/compare/compare-artworks`
        : `${API_BASE}/api/compare/compare-artworks`;

      const result = await enqueueAndPoll(
        compareEndpoint,
        { artwork1: a1, artwork2: a2 },
        {
          interval: 2000,
          maxAttempts: 150,
          onProgress: (status) => {
            if (status === 'processing') setComparisonError(null);
          },
        }
      );
      setComparisonResult(result);
      if (result && user?.uid && primaryArtwork && comparisonArtwork && currentFlowId) {
        saveFlowStep2(user.uid, primaryArtwork, currentFlowId, comparisonArtwork, result).catch(() => {});
      }
    } catch (err) {
      console.error('[LearnArtHistoryFlow] Compare error:', err);
      const msg =
        err?.message?.includes('timeout') || err?.name === 'AbortError'
          ? 'Comparison is taking longer than expected. Please try again.'
          : err?.message || 'Failed to compare artworks. Please try again.';
      setComparisonError(msg);
    } finally {
      setIsComparing(false);
    }
  };

  const handlePointClick = (idx) => {
    setExpandedPoint((prev) => (prev === idx ? null : idx));
  };

  const handleStep2Complete = () => {
    setStep2Complete(true);
    setLetsGoModalStep(2);
    setShowLetsGoModal(true);
  };

  const toggleStep3Section = (sectionKey) => {
    setStep3SectionsOpen((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  const handleUploadFlowActivity = async (shareToCoLearning) => {
    if (!(step1Complete && step2Complete)) {
      alert('Please complete Step 1 and Step 2 before uploading your art history submission.');
      return;
    }
    if (!activityFile || !user) return;
    const a = step3Activity;
    const flowKey = buildFlowActivityKey(primaryArtwork, comparisonArtwork, a?.title);
    const uploadEndpoint = API_BASE.endsWith('/api')
      ? `${API_BASE}/upload-art-history-activity`
      : `${API_BASE}/api/upload-art-history-activity`;
    const formData = new FormData();
    formData.append('file', activityFile);
    formData.append('public', String(shareToCoLearning));
    formData.append('uid', user.uid);
    formData.append('userName', user.displayName || user.email || 'Anonymous');
    formData.append('activityType', 'flow-activity');
    formData.append('artworkId', flowKey);
    formData.append('artworkTitle', a?.title || 'Flow Activity');
    formData.append('artworkArtist', '');
    formData.append('artworkMovement', '');
    formData.append('artworkImageUrl', '');
    formData.append('flowActivityKey', flowKey);
    formData.append('flowActivityTitle', a?.title || '');
    formData.append('flowActivityObjective', a?.objective || a?.whatYoullMake || '');
    formData.append('artwork1ImageUrl', getImageUrl(primaryArtwork) || '');
    formData.append('artwork2ImageUrl', getImageUrl(comparisonArtwork) || '');
    formData.append('artwork1Title', primaryArtwork?.title || '');
    formData.append('artwork2Title', comparisonArtwork?.title || '');
    formData.append('artworkTypeKey', effectiveArtworkTypeKey || '');
    formData.append('artworkTypeLabel', effectiveArtworkTypeLabel || '');
    formData.append('artworkLevel', String(effectiveLevel || 1));
    formData.append('step1Complete', String(step1Complete));
    formData.append('step2Complete', String(step2Complete));

    setIsUploading(true);
    setShowShareConfirmation(false);
    try {
      const response = await fetch(uploadEndpoint, { method: 'POST', body: formData });
      if (!response.ok) {
        if (response.status === 404) {
          alert('Upload service is temporarily unavailable. Please try again later or contact support.');
          return;
        }
        let errMsg = 'Upload failed';
        try {
          const err = await response.json();
          errMsg = err.error || errMsg;
        } catch (_) {}
        throw new Error(errMsg);
      }
      const result = await response.json();
      setUploadSuccessData({ shared: shareToCoLearning, artworkTitle: a?.title });
      setShowSuccessPopup(true);
      setActivityFile(null);
      if (user?.uid && primaryArtwork) {
        if (currentFlowId) markFlowUploaded(user.uid, primaryArtwork, currentFlowId).catch(() => {});
      }
    } catch (e) {
      alert(e?.message || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div
      className="flex h-screen flex-col overflow-hidden bg-gradient-to-b from-[#fff8ef] via-[#fff4e4] to-[#fdebd0]"
    >
      {/* Step 3 activity loader: show only on Step 2 while job runs; frosted overlay */}
      {step3Loading && stepIndex === 1 && (
        <div className="fixed inset-0 flex items-center justify-center z-[9999] bg-stone-900/40 backdrop-blur-xl" aria-live="polite">
          <div className="bg-white rounded-xl shadow-2xl px-8 py-8 flex flex-col items-center mx-4 border-4 border-amber-300">
            <svg className="animate-spin h-12 w-12 text-amber-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            <div className="text-amber-700 font-bold text-lg text-center mb-2">Artmosphere is creating your activity</div>
            <div className="text-amber-600 text-sm text-center">Please wait while we combine both artworks into a fun activity</div>
          </div>
        </div>
      )}

      {/* Step 1: compact header, slimmer pills, 2-col content (artwork hero + Discovery Panel) */}
      {stepIndex === 0 ? (
        <main className="flex min-h-0 flex-1 flex-col items-center overflow-hidden px-3 py-3 md:px-6 md:py-4">
          <div className={`${FLOW_SHELL_CLASS} overflow-y-auto`}>
            <div className="flex shrink-0 items-center justify-between gap-2 border-b border-[#efe2d0]/80 bg-white/70 px-4 py-3 backdrop-blur-sm md:px-6">
              <button
                type="button"
                onClick={handleBack}
                className="flex items-center gap-1.5 rounded-full border border-[#e8d4bc] bg-white px-3 py-2 text-sm font-medium text-[#2F251B] shadow-sm transition hover:bg-[#fff9ef]"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
              <button
                type="button"
                onClick={() => navigate('/my-art-world')}
                className="flex items-center gap-1.5 rounded-full border border-[#e8d4bc] bg-white px-3 py-2 text-sm font-medium text-[#2F251B] shadow-sm transition hover:bg-[#fff9ef] md:px-4"
              >
                <LayoutDashboard className="h-4 w-4" />
                <span className="hidden md:inline">Artist Corner</span>
              </button>
            </div>
            <div className="shrink-0 border-b border-[#efe2d0]/60 px-4 py-3 md:px-6">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8A7357]">
                Learn art history
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {STEP_LABELS.map((label, i) => {
                  const canGo = i <= stepIndex || (i === 1 && step1Complete) || (i === 2 && step2Complete);
                  const active = i === stepIndex;
                  const done = i < stepIndex;
                  const pillClass = `rounded-full px-3 py-1.5 text-xs font-semibold transition sm:text-sm sm:px-4 sm:py-2 ${flowStepPillClass(active, done, !canGo)}`;
                  return canGo ? (
                    <button key={i} type="button" onClick={() => setStepIndex(i)} className={pillClass}>
                      <span className="opacity-70">Step {i + 1}</span>
                      <span className="mx-1.5 hidden sm:inline">·</span>
                      <span>{label}</span>
                    </button>
                  ) : (
                    <span key={i} className={pillClass}>
                      <span className="opacity-70">Step {i + 1}</span>
                      <span className="mx-1.5 hidden sm:inline">·</span>
                      <span>{label}</span>
                    </span>
                  );
                })}
              </div>
              {primaryArtwork && (
                <p className="mt-2 truncate text-xs text-[#8A7357]">
                  {primaryArtwork.title || 'Untitled'}
                  {effectiveArtworkTypeLabel ? ` · ${effectiveArtworkTypeLabel}` : ''}
                  {effectiveLevel != null ? ` · Level ${effectiveLevel}` : ''}
                </p>
              )}
            </div>

            {isResumeEntry && !flowLoaded ? (
              <div className="flex-1 min-h-0 flex items-center justify-center px-4 md:px-6 py-6">
                <div className="bg-white/85 border border-amber-200 rounded-2xl px-6 py-5 shadow-sm flex items-center gap-3 text-amber-800">
                  <svg className="animate-spin h-5 w-5 text-amber-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  <span className="text-sm font-medium">Loading your saved Step 1 analysis...</span>
                </div>
              </div>
            ) : (
              <>
                {/* Pre-analysis: artwork + Analyze button in one column that fills space; artwork grows to fill gap */}
                {primaryArtwork && !step1AnalysisComplete && (
                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-2 pt-3 md:px-6">
                    <div className="mx-auto flex min-h-0 w-full max-w-4xl flex-1 flex-col overflow-hidden rounded-2xl border border-[#e8d4bc]/90 shadow-[0_12px_40px_rgba(120,72,20,0.1)]">
                      <div className="relative min-h-[min(48vh,520px)] flex-1">
                        {previewHeroSrc || fullHeroSrc ? (
                          <>
                            <ArtworkFillDisplay
                              src={fullHeroDecoded ? fullHeroSrc || previewHeroSrc : previewHeroSrc || fullHeroSrc}
                              alt={primaryArtwork.title || 'Selected artwork'}
                              containerClassName="absolute inset-0 h-full w-full"
                            />
                            {previewHeroSrc && fullHeroSrc && !fullHeroDecoded && (
                              <img
                                src={fullHeroSrc}
                                alt=""
                                aria-hidden
                                className="pointer-events-none absolute h-0 w-0 opacity-0"
                                onLoad={() => setFullHeroDecoded(true)}
                              />
                            )}
                          </>
                        ) : (
                          <div className="flex h-full min-h-[240px] items-center justify-center bg-gradient-to-br from-[#fff8ef] to-[#fdebd0] text-sm text-[#8A7357]">
                            No image
                          </div>
                        )}
                      </div>
                      <div className="shrink-0 border-t border-[#f0e6d8]/90 bg-white/85 px-4 py-3 backdrop-blur-sm">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8A7357]">
                          Artwork
                        </p>
                        <h3 className="mt-0.5 truncate text-base font-semibold text-[#2F251B]">
                          {primaryArtwork.title || 'Untitled'}
                        </h3>
                        <p className="mt-0.5 truncate text-sm text-[#5e5a54]">
                          {primaryArtwork.artistName || primaryArtwork.artist || 'Unknown'}
                          {primaryArtwork.artMovement ? ` · ${primaryArtwork.artMovement}` : ''}
                        </p>
                        <p className="mt-1.5 text-xs text-[#8A7357]">
                          <span className="font-semibold">Category</span>
                          {' · '}
                          <span className="text-[#2F251B]">
                            {effectiveArtworkTypeLabel ||
                              primaryArtwork.typeOfArtwork ||
                              primaryArtwork.artworkType ||
                              '—'}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Appreciation: when analysis done show 2-col; when Step 1 complete don't grow — keep "Continue" button right below content */}
                <div
                  className={`flex flex-col px-4 md:px-6 pt-1 pb-0 ${
                    step1Complete && stepIndex === 0
                      ? 'flex-shrink-0'
                      : step1AnalysisComplete
                      ? 'flex-1 min-h-0'
                      : 'flex-shrink-0'
                  }`}
                >
                  <ArtHistoryAppreciation
                    primaryArtwork={primaryArtwork}
                    step1AlreadyComplete={step1Complete && stepIndex === 0}
                    initialAppreciationResult={step1AppreciationResult}
                    onAppreciationResult={setStep1AppreciationResult}
                    onAnalysisComplete={() => {
                      setStep1AnalysisComplete(true);
                      if (user?.uid && primaryArtwork && !currentFlowId) {
                        saveFlowStep1(user.uid, primaryArtwork, {}, {
                          artworkTypeKey: effectiveArtworkTypeKey ?? undefined,
                          level: effectiveLevel ?? undefined,
                          artworkTypeLabel: effectiveArtworkTypeLabel || undefined,
                        })
                          .then((flowId) => {
                            if (flowId) setCurrentFlowId(flowId);
                          })
                          .catch((err) => {
                            console.error('[LearnArtHistoryFlow] Step 1 auto-save failed:', err);
                            setStep1SaveError(
                              err?.message || 'Progress will save when you click Mark as Done.'
                            );
                          });
                      }
                    }}
                    onStep1Complete={handleStep1Complete}
                  />
                </div>

                {/* Continue to remaining flow — when Step 1 is already complete (e.g. resuming), let user jump to Step 2 */}
                {step1Complete && stepIndex === 0 && (
                  <div className="flex shrink-0 flex-col items-center justify-center gap-2 border-t border-[#efe2d0]/80 bg-[#fff9ef]/50 px-4 py-4 sm:flex-row md:px-6">
                    <span className="text-center text-sm font-medium text-[#5e5a54] sm:text-left">
                      You&apos;ve completed Step 1. Continue where you left off.
                    </span>
                    <button
                      type="button"
                      onClick={() => setStepIndex(1)}
                      className="shrink-0 rounded-full border border-[#f5d99b] bg-gradient-to-r from-[#fde68a] to-[#fbbf24] px-5 py-2 text-sm font-semibold text-[#2F251B] shadow-sm transition hover:from-[#fcd34d] hover:to-[#f59e0b]"
                    >
                      Continue to remaining flow
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      ) : (
        <>
          <header className="sticky top-0 z-30 shrink-0 border-b border-[#efe2d0]/80 bg-white/75 backdrop-blur-md">
            <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-3 md:px-6">
              <div className="flex items-center justify-between gap-2">
                <button type="button" onClick={handleBack} className="flex items-center gap-1.5 rounded-full border border-[#e8d4bc] bg-white px-3 py-2 text-sm font-medium text-[#2F251B] shadow-sm transition hover:bg-[#fff9ef]">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
                <button type="button" onClick={() => navigate('/my-art-world')} className="flex items-center gap-1.5 rounded-full border border-[#e8d4bc] bg-white px-3 py-2 text-sm font-medium text-[#2F251B] shadow-sm transition hover:bg-[#fff9ef] md:px-4">
                  <LayoutDashboard className="h-4 w-4" />
                  <span className="hidden md:inline">Artist Corner</span>
                </button>
              </div>
              <div>
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8A7357]">Learn art history</p>
                <div className="flex flex-wrap items-center gap-2">
                  {STEP_LABELS.map((label, i) => {
                    const canGo = i <= stepIndex || (i === 1 && step1Complete) || (i === 2 && step2Complete);
                    const active = i === stepIndex;
                    const done = i < stepIndex;
                    const pillClass = `rounded-full px-3 py-1.5 text-xs font-semibold transition sm:text-sm sm:px-4 sm:py-2 ${flowStepPillClass(active, done, !canGo)}`;
                    return canGo ? (
                      <button key={i} type="button" onClick={() => setStepIndex(i)} className={pillClass}>
                        <span className="opacity-70">Step {i + 1}</span>
                        <span className="mx-1.5 hidden sm:inline">·</span>
                        <span>{label}</span>
                      </button>
                    ) : (
                      <span key={i} className={pillClass}>
                        <span className="opacity-70">Step {i + 1}</span>
                        <span className="mx-1.5 hidden sm:inline">·</span>
                        <span>{label}</span>
                      </span>
                    );
                  })}
                </div>
              </div>
              {primaryArtwork && (
                <div className="flex items-center gap-3 border-t border-[#efe2d0]/60 py-2">
                  <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-[#e8d4bc]/80">
                    {previewHeroSrc || fullHeroSrc ? (
                      <img src={previewHeroSrc || fullHeroSrc} alt="" className="h-full w-full object-cover" loading="eager" fetchpriority="high" decoding="sync" />
                    ) : (
                      <div className="h-full w-full bg-[#f4efe6]" />
                    )}
                  </div>
                  <div className="min-w-0 truncate text-sm font-semibold text-[#2F251B]">{primaryArtwork.title || 'Untitled'}</div>
                </div>
              )}
            </div>
          </header>
          <main className="min-h-0 flex-1 overflow-auto px-3 py-4 md:px-6 md:py-5">

        {stepIndex === 1 && (
          <>
            {/* Loading overlay — frosted, sophisticated dimming */}
            {(isComparing) && (
              <div className="fixed inset-0 flex items-center justify-center z-[9999] bg-stone-900/40 backdrop-blur-xl">
                <div className="bg-white rounded-xl shadow-2xl px-8 py-8 flex flex-col items-center mx-4 border-4 border-amber-300">
                  <svg className="animate-spin h-12 w-12 text-amber-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  <div className="text-amber-700 font-bold text-base text-center max-w-[420px] leading-relaxed">
                    {(() => {
                      const leftTitle = primaryArtwork?.title || 'your first artwork';
                      const rightTitle = comparisonArtwork?.title || 'the artwork you picked to compare';
                      return (
                        <>
                          <div>{`Hold on tight! Artmosphere is looking closely at “${leftTitle}” and “${rightTitle}”.`}</div>
                          <div>{'Let’s see how these two artworks talk to each other.'}</div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            <div className={`${FLOW_SHELL_CLASS} mx-auto overflow-hidden`}>
            <div className="px-4 md:px-6 py-6 flex-1">
              <p className="text-gray-700 mb-6">
                Compare your chosen artwork with another {effectiveArtworkTypeLabel || 'artwork'} to see similarities and differences in technique and style.
              </p>

              {/* Always show full artwork grid (two cards); buttons go below when comparison exists */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Primary artwork */}
                <div className="bg-white/90 rounded-2xl p-6 border-2 border-amber-200/60 shadow-lg">
                  <h3 className="text-lg font-bold text-amber-800 mb-3">Your artwork</h3>
                  {primaryArtwork && (
                    <>
                      <div className="aspect-[4/3] w-full rounded-lg overflow-hidden bg-amber-50 mb-3">
                        {primaryArtwork.imageUrl ? (
                          <img src={primaryArtwork.imageUrl} alt={primaryArtwork.title || 'Artwork'} className="w-full h-full object-contain" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-amber-600 text-sm">No image</div>
                        )}
                      </div>
                      <p className="font-semibold text-gray-800">{primaryArtwork.title || 'Untitled'}</p>
                      <p className="text-sm text-gray-600">{primaryArtwork.artistName || primaryArtwork.artist || 'Unknown'}</p>
                      {primaryArtwork.artMovement && <p className="text-xs text-amber-600">{primaryArtwork.artMovement}</p>}
                    </>
                  )}
                </div>

                {/* Comparison artwork */}
                <div className="bg-white/90 rounded-2xl p-6 border-2 border-dashed border-amber-300/70 shadow-lg">
                  <h3 className="text-lg font-bold text-amber-800 mb-3">Artwork to compare with</h3>
                  {comparisonArtwork ? (
                    <>
                      <div className="aspect-[4/3] w-full rounded-lg overflow-hidden bg-amber-50 mb-3">
                        <img src={comparisonArtwork.imageUrl} alt={comparisonArtwork.title || 'Artwork'} className="w-full h-full object-contain" />
                      </div>
                      <p className="font-semibold text-gray-800">{comparisonArtwork.title || 'Untitled'}</p>
                      <p className="text-sm text-gray-600">{comparisonArtwork.artistName || comparisonArtwork.artist || 'Unknown'}</p>
                      {comparisonArtwork.artMovement && <p className="text-xs text-amber-600">{comparisonArtwork.artMovement}</p>}
                      <button
                        type="button"
                        onClick={() => setShowCompareModal(true)}
                        className="mt-3 w-full py-2 px-4 rounded-xl text-sm font-medium bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200 transition-colors"
                      >
                        Change artwork
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowCompareModal(true)}
                      className="w-full aspect-[4/3] rounded-lg border-2 border-dashed border-amber-300 flex flex-col items-center justify-center text-amber-600 hover:border-amber-400 hover:bg-amber-50/50 transition-colors"
                    >
                      <span className="text-4xl mb-2">+</span>
                      <span className="font-semibold">Choose artwork to compare</span>
                    </button>
                  )}
                </div>
              </div>

              {comparisonError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-center text-sm">
                  {comparisonError}
                </div>
              )}

              {!comparisonResult ? (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleCompare}
                    disabled={!comparisonArtwork || isComparing}
                    className="py-3 px-8 rounded-xl text-base font-semibold text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2"
                    style={{
                      background: comparisonArtwork && !isComparing ? 'linear-gradient(180deg, #FFE082 0%, #FFD54F 100%)' : '#E5E7EB',
                      boxShadow: comparisonArtwork && !isComparing ? '0 2px 8px rgba(255, 193, 7, 0.35)' : 'none',
                    }}
                  >
                    {isComparing ? 'Comparing...' : 'Compare Artworks'}
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap justify-center gap-3 mb-4">
                    <button
                      type="button"
                      onClick={() => { setVisibleSection('similarities'); setExpandedPoint(null); }}
                      className={`px-6 py-3 rounded-xl font-semibold border-2 transition-colors ${visibleSection === 'similarities' ? 'text-white bg-amber-500 border-amber-400' : 'text-amber-800 bg-amber-50 border-amber-200 hover:bg-amber-100'}`}
                    >
                      Similarities
                    </button>
                    <button
                      type="button"
                      onClick={() => { setVisibleSection('differences'); setExpandedPoint(null); }}
                      className={`px-6 py-3 rounded-xl font-semibold border-2 transition-colors ${visibleSection === 'differences' ? 'text-white bg-orange-500 border-orange-400' : 'text-orange-800 bg-orange-50 border-orange-200 hover:bg-orange-100'}`}
                    >
                      Differences
                    </button>
                    <button
                      type="button"
                      onClick={() => { setVisibleSection('insight'); setExpandedPoint(null); }}
                      className={`px-6 py-3 rounded-xl font-semibold border-2 transition-colors ${visibleSection === 'insight' ? 'text-white bg-emerald-500 border-emerald-400' : 'text-emerald-800 bg-emerald-50 border-emerald-200 hover:bg-emerald-100'}`}
                    >
                      Learning Insight
                    </button>
                  </div>

                  {/* Inline comparison content (same UI as rest of flow — no modal) */}
                  {visibleSection && (
                    <div className="rounded-2xl border-2 border-amber-200 bg-white shadow-lg overflow-hidden mb-6">
                      <div className="px-4 py-3 bg-gradient-to-r from-amber-100 to-amber-50 border-b border-amber-200">
                        <h3 className="text-amber-900 font-bold">
                          {visibleSection === 'similarities' && 'Similarities'}
                          {visibleSection === 'differences' && 'Differences'}
                          {visibleSection === 'insight' && 'Learning Insight'}
                        </h3>
                      </div>
                      <div className="p-6 overflow-auto max-h-[50vh]">
                        {visibleSection === 'insight' && comparisonResult.insight && (
                          <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">{comparisonResult.insight}</p>
                        )}
                        {(visibleSection === 'similarities' || visibleSection === 'differences') && (() => {
                          const items = visibleSection === 'similarities'
                            ? (Array.isArray(comparisonResult.similarities) ? comparisonResult.similarities : [comparisonResult.similarities].filter(Boolean))
                            : (Array.isArray(comparisonResult.differences) ? comparisonResult.differences : [comparisonResult.differences].filter(Boolean));
                          if (!items?.length) return <p className="text-gray-600">No content.</p>;
                          if (expandedPoint !== null && items[expandedPoint]) {
                            return (
                              <div>
                                <p className="text-sm font-semibold text-amber-800 mb-2">
                                  {visibleSection === 'similarities' ? 'Similarity' : 'Difference'} {expandedPoint + 1}:
                                </p>
                                <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">{items[expandedPoint]}</p>
                                <div className="flex flex-wrap gap-2 mt-4">
                                  {items.map((_, idx) => (
                                    <button
                                      key={idx}
                                      type="button"
                                      onClick={() => handlePointClick(idx)}
                                      className={`px-3 py-1.5 rounded-lg font-medium text-sm ${expandedPoint === idx ? 'bg-amber-600 text-white' : 'bg-amber-100 text-amber-800 hover:bg-amber-200'}`}
                                    >
                                      {idx + 1}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div>
                              <p className="text-gray-600 mb-4">Click a number to explore each {visibleSection === 'similarities' ? 'similarity' : 'difference'}.</p>
                              <div className="flex flex-wrap gap-2">
                                {items.map((_, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    onClick={() => handlePointClick(idx)}
                                    className="art-btn-primary px-4 py-2 rounded-lg font-semibold transition-colors"
                                  >
                                    {idx + 1}
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {!step2Complete && (
                    <div className="text-center pt-4">
                      <button
                        type="button"
                        onClick={handleStep2Complete}
                        className="py-2.5 px-6 rounded-xl text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2"
                        style={{ background: 'linear-gradient(180deg, #FFE082 0%, #FFD54F 100%)', boxShadow: '0 2px 8px rgba(255, 193, 7, 0.35)' }}
                      >
                        Mark as Done
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
            </div>

            <ChooseArtworkModal
              isOpen={showCompareModal}
              onClose={() => setShowCompareModal(false)}
              artworkTypeKey={effectiveArtworkTypeKey}
              level={effectiveLevel}
              artworkTypeLabel={effectiveArtworkTypeLabel}
              onSelectAndStart={handleCompareArtworkSelect}
              excludeId={primaryArtwork?.id}
              mode="compare"
            />
          </>
        )}

        {stepIndex === 2 && (
          <div className="flex justify-center items-start py-2">
            {/* Step 3: refined window — contained panel, not full page */}
            <div
              className="w-full max-w-4xl rounded-3xl overflow-hidden flex flex-col shadow-2xl"
              style={{
                boxShadow: '0 0 0 4px rgba(255, 235, 180, 0.95), 0 0 0 8px rgba(245, 158, 11, 0.15), 0 25px 50px -12px rgba(0, 0, 0, 0.15)',
                background: 'linear-gradient(165deg, #FFFEF9 0%, #FFF9E6 50%, #FFF8E8 100%)',
                border: '3px solid rgba(251, 191, 36, 0.5)',
              }}
            >
              {/* Window title bar */}
              <div className="flex-shrink-0 px-6 md:px-8 py-4 border-b border-amber-200/70 bg-white/60 rounded-t-3xl">
                <div className="flex items-center justify-between gap-4">
                  <h2 className="text-lg md:text-xl font-bold text-amber-900">Step 3: Try an Activity</h2>
                  <button
                    type="button"
                    onClick={() => setStepIndex(1)}
                    className="text-sm font-medium text-amber-700 hover:text-amber-800 hover:bg-amber-100/80 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-1 rounded-lg px-3 py-2 transition-colors"
                  >
                    ← Back to Step 2
                  </button>
                </div>
              </div>

              {/* Window content */}
              <div className="flex-1 overflow-auto p-6 md:p-8">
                {step3Error && (
                  <div className="mb-6 p-4 bg-red-50/90 border border-red-200 rounded-xl text-red-700 text-center">
                    {step3Error}
                    <button
                      type="button"
                      onClick={() => { setStep3Error(null); setStep3Activity(null); }}
                      className="block mx-auto mt-2 text-sm font-medium text-red-600 hover:underline"
                    >
                      Try again
                    </button>
                  </div>
                )}

                {step3Activity && !step3Loading && (
                  <div className="space-y-6">
                    {/* Artwork pair — two cards side by side */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                      {primaryArtwork && (
                        <div
                          className="rounded-2xl overflow-hidden bg-white border-2 border-amber-200/80 shadow-md"
                          style={{ boxShadow: 'inset 0 0 0 1px rgba(251, 191, 36, 0.2), 0 4px 14px rgba(0,0,0,0.06)' }}
                        >
                          <div className="aspect-[4/3] max-h-[200px] w-full flex items-center justify-center bg-amber-50/50">
                            {primaryArtwork.imageUrl ? (
                              <img src={primaryArtwork.imageUrl} alt={primaryArtwork.title || 'Artwork 1'} className="w-full h-full object-contain" />
                            ) : (
                              <span className="text-amber-600 text-sm">No image</span>
                            )}
                          </div>
                          <div className="px-4 py-3 border-t border-amber-100 bg-white">
                            <p className="font-semibold text-gray-800 text-sm truncate">{primaryArtwork.title || 'Untitled'}</p>
                            <p className="text-xs text-gray-600 truncate mt-0.5">{primaryArtwork.artistName || primaryArtwork.artist || 'Unknown'}</p>
                          </div>
                        </div>
                      )}
                      {comparisonArtwork && (
                        <div
                          className="rounded-2xl overflow-hidden bg-white border-2 border-amber-200/80 shadow-md"
                          style={{ boxShadow: 'inset 0 0 0 1px rgba(251, 191, 36, 0.2), 0 4px 14px rgba(0,0,0,0.06)' }}
                        >
                          <div className="aspect-[4/3] max-h-[200px] w-full flex items-center justify-center bg-amber-50/50">
                            {comparisonArtwork.imageUrl ? (
                              <img src={comparisonArtwork.imageUrl} alt={comparisonArtwork.title || 'Artwork 2'} className="w-full h-full object-contain" />
                            ) : (
                              <span className="text-amber-600 text-sm">No image</span>
                            )}
                          </div>
                          <div className="px-4 py-3 border-t border-amber-100 bg-white">
                            <p className="font-semibold text-gray-800 text-sm truncate">{comparisonArtwork.title || 'Untitled'}</p>
                            <p className="text-xs text-gray-600 truncate mt-0.5">{comparisonArtwork.artistName || comparisonArtwork.artist || 'Unknown'}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Activity card — title, objective, materials, beforeYouStart, steps, techniqueMix, keyTips, bonusChallenge, tinyReflection */}
                    {(() => {
                      const a = step3Activity;
                      const canUploadFlowSubmission = step1Complete && step2Complete;
                      const stepsList = Array.isArray(a.steps) ? a.steps : (Array.isArray(a.stepByStep) ? a.stepByStep : []);
                      const objectiveText = a.objective || a.whatYoullMake || '';
                      const beforeYouStart = Array.isArray(a.beforeYouStart) ? a.beforeYouStart : [];
                      const techniqueMix = Array.isArray(a.techniqueMix) ? a.techniqueMix : [];
                      const keyTips = Array.isArray(a.keyTips) ? a.keyTips : [];
                      const bonusChallenge = a.bonusChallenge;
                      const tinyReflection = Array.isArray(a.tinyReflection) ? a.tinyReflection : [];
                      return (
                    <div
                      className="rounded-2xl overflow-hidden bg-white border-2 border-amber-200/80 p-6 md:p-8"
                      style={{ boxShadow: 'inset 0 0 0 1px rgba(251, 191, 36, 0.15), 0 8px 24px rgba(0,0,0,0.08)' }}
                    >
                      <h3 className="text-xl font-bold text-amber-900 mb-3" style={{ color: 'rgb(120, 53, 15)' }}>
                        {a.title}
                      </h3>
                      {objectiveText && (
                        <p className="text-gray-700 text-[15px] leading-relaxed mb-5">{objectiveText}</p>
                      )}
                      {a.materials && a.materials.length > 0 && (
                        <div className="mb-5 rounded-xl border border-amber-200/80 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => toggleStep3Section('materials')}
                            className="w-full px-4 py-3 bg-amber-50/70 text-left flex items-center justify-between"
                          >
                            <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide text-amber-800/90">Materials</h4>
                            <span className="text-amber-700">{step3SectionsOpen.materials ? '−' : '+'}</span>
                          </button>
                          {step3SectionsOpen.materials && (
                            <div className="px-4 py-3">
                              <ul className="space-y-1.5">
                                {a.materials.map((m, i) => (
                                  <li key={i} className="text-gray-700 text-sm flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                                    {m}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                      {beforeYouStart.length > 0 && (
                        <div className="mb-5 rounded-xl border border-amber-200/80 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => toggleStep3Section('before')}
                            className="w-full px-4 py-3 bg-amber-50/70 text-left flex items-center justify-between"
                          >
                            <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide text-amber-800/90">Before You Start</h4>
                            <span className="text-amber-700">{step3SectionsOpen.before ? '−' : '+'}</span>
                          </button>
                          {step3SectionsOpen.before && (
                            <div className="px-4 py-3">
                              <ul className="space-y-1.5">
                                {beforeYouStart.map((item, i) => (
                                  <li key={i} className="text-gray-700 text-sm flex items-start gap-2">
                                    <span className="text-amber-500 mt-1.5">•</span>
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                      {stepsList.length > 0 && (
                        <div className="mb-5 rounded-xl border border-amber-200/80 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => toggleStep3Section('steps')}
                            className="w-full px-4 py-3 bg-amber-50/70 text-left flex items-center justify-between"
                          >
                            <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide text-amber-800/90">Step-by-Step</h4>
                            <span className="text-amber-700">{step3SectionsOpen.steps ? '−' : '+'}</span>
                          </button>
                          {step3SectionsOpen.steps && (
                            <div className="px-4 py-3">
                              <div className="space-y-2">
                                {stepsList.map((step, index) => {
                                  const isExpanded = expandedActivitySteps[index];
                                  const previewText = typeof step === 'string' && step.length > 70 ? step.substring(0, 70) + '…' : step;
                                  return (
                                    <div
                                      key={index}
                                      className="rounded-xl overflow-hidden border border-amber-200/80 bg-white/90"
                                      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                                    >
                                      <button
                                        type="button"
                                        onClick={() => setExpandedActivitySteps((prev) => ({ ...prev, [index]: !prev[index] }))}
                                        className="w-full px-4 py-3 text-left flex items-center justify-between gap-3 hover:bg-amber-50/70 transition-colors rounded-xl"
                                      >
                                        <div className="flex items-start gap-3 flex-1 min-w-0">
                                          <span className="bg-amber-500 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                            {index + 1}
                                          </span>
                                          <span className="text-gray-800 text-sm flex-1 leading-relaxed break-words">
                                            {isExpanded ? step : previewText}
                                          </span>
                                        </div>
                                        <svg
                                          className={`w-4 h-4 text-amber-600 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                      </button>
                                      {isExpanded && (
                                        <div className="px-4 pb-3 pt-1 bg-amber-50/50 border-t border-amber-200/60">
                                          <p className="text-xs text-amber-700/80 italic pl-10">Refer back to both artworks for inspiration.</p>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {techniqueMix.length > 0 && (
                        <div className="mb-5 rounded-xl border border-amber-200/80 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => toggleStep3Section('techniqueMix')}
                            className="w-full px-4 py-3 bg-amber-50/70 text-left flex items-center justify-between"
                          >
                            <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide text-amber-800/90">Technique Mix</h4>
                            <span className="text-amber-700">{step3SectionsOpen.techniqueMix ? '−' : '+'}</span>
                          </button>
                          {step3SectionsOpen.techniqueMix && (
                            <div className="px-4 py-3">
                              <ul className="space-y-2">
                                {techniqueMix.map((item, i) => (
                                  <li key={i} className="text-gray-700 text-sm flex items-start gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0 mt-1.5" />
                                    <span className="whitespace-pre-wrap break-words">{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                      {keyTips.length > 0 && (
                        <div className="mb-5 rounded-xl border border-amber-200/80 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => toggleStep3Section('keyTips')}
                            className="w-full px-4 py-3 bg-amber-50/70 text-left flex items-center justify-between"
                          >
                            <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide text-amber-800/90">Key Tips</h4>
                            <span className="text-amber-700">{step3SectionsOpen.keyTips ? '−' : '+'}</span>
                          </button>
                          {step3SectionsOpen.keyTips && (
                            <div className="px-4 py-3">
                              <ul className="space-y-1.5">
                                {keyTips.map((item, i) => (
                                  <li key={i} className="text-gray-700 text-sm flex items-start gap-2">
                                    <span className="text-amber-600 font-medium flex-shrink-0">Tip {i + 1}.</span>
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                      {bonusChallenge && (
                        <div className="mb-5 rounded-xl border border-amber-200/80 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => toggleStep3Section('bonus')}
                            className="w-full px-4 py-3 bg-amber-50/70 text-left flex items-center justify-between"
                          >
                            <h4 className="font-semibold text-amber-900 text-sm uppercase tracking-wide">Bonus Challenge</h4>
                            <span className="text-amber-700">{step3SectionsOpen.bonus ? '−' : '+'}</span>
                          </button>
                          {step3SectionsOpen.bonus && (
                            <div className="px-4 py-3 bg-amber-50/50">
                              <p className="text-gray-700 text-sm">{bonusChallenge}</p>
                            </div>
                          )}
                        </div>
                      )}
                      {tinyReflection.length > 0 && (
                        <div className="mb-5 rounded-xl border border-amber-200/80 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => toggleStep3Section('reflection')}
                            className="w-full px-4 py-3 bg-amber-50/70 text-left flex items-center justify-between"
                          >
                            <h4 className="font-semibold text-amber-900 text-sm uppercase tracking-wide">Tiny Reflection</h4>
                            <span className="text-amber-700">{step3SectionsOpen.reflection ? '−' : '+'}</span>
                          </button>
                          {step3SectionsOpen.reflection && (
                            <div className="px-4 py-3 bg-amber-50/50">
                              <ul className="space-y-1.5">
                                {tinyReflection.map((q, i) => (
                                  <li key={i} className="text-gray-700 text-sm italic">&ldquo;{q}&rdquo;</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Artwork upload at end of activity */}
                      <div className="mt-8 pt-5 border-t border-amber-200/80">
                        <p className="text-gray-700 font-medium mb-3">Upload your activity artwork</p>
                        <div className="flex flex-wrap items-center gap-3 mb-3">
                          <label className="cursor-pointer">
                            <span className={`inline-block py-2 px-4 rounded-xl text-sm font-medium border-2 ${canUploadFlowSubmission ? 'border-amber-300 text-amber-800 hover:bg-amber-50' : 'border-gray-300 text-gray-500 bg-gray-100 cursor-not-allowed'}`}>
                              {activityFile ? activityFile.name : 'Choose image'}
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              disabled={!canUploadFlowSubmission || isUploading}
                              onChange={(e) => setActivityFile(e.target.files?.[0] || null)}
                            />
                          </label>
                          <button
                            type="button"
                            disabled={!canUploadFlowSubmission || !activityFile || isUploading}
                            onClick={() => setShowShareConfirmation(true)}
                            className="py-2.5 px-6 rounded-xl text-sm font-semibold text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2"
                            style={{ background: 'linear-gradient(180deg, #FFE082 0%, #FFD54F 100%)', boxShadow: '0 2px 8px rgba(255, 193, 7, 0.35)' }}
                          >
                            {isUploading ? 'Uploading…' : 'Upload'}
                          </button>
                        </div>
                        {!canUploadFlowSubmission && (
                          <p className="text-amber-700 text-sm mb-2">
                            Upload unlocks only after you complete Step 1 (analysis) and Step 2 (compare).
                          </p>
                        )}
                        <p className="text-gray-600 text-sm mb-4">
                          If only Step 1 is done, your analysis is saved. Compare + activity submission is available after Step 2.
                        </p>
                        <div className="text-center">
                          <button
                            type="button"
                            onClick={() => navigate('/learn-art-history')}
                            className="py-2 px-4 rounded-xl text-sm font-medium text-amber-700 hover:bg-amber-100 border border-amber-200"
                          >
                            Finish without uploading
                          </button>
                        </div>
                      </div>
                    </div>
                      );
                    })()}
                  </div>
                )}

                {!step3Activity && !step3Loading && !step3Error && (
                  <div className="text-center py-12 text-amber-700/80">Preparing your activity…</div>
                )}
              </div>
            </div>

            {/* Share confirmation: share to Art History Learning Co-Learning Space? */}
            {showShareConfirmation && (
              <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 border-2 border-amber-200">
                  <h3 className="text-lg font-bold text-amber-900 mb-2">Share to Co-Learning Space?</h3>
                  <p className="text-gray-700 text-sm mb-6">
                    Would you like to share your artwork to the Art History Learning Co-Learning Space? Other artists who attempted this activity will see it.
                  </p>
                  <div className="flex gap-3 justify-end">
                    <button
                      type="button"
                      disabled={isUploading}
                      onClick={() => handleUploadFlowActivity(false)}
                      className="px-4 py-2 rounded-xl border border-amber-200 text-amber-700 bg-white hover:bg-amber-50 disabled:opacity-50"
                    >
                      Upload only
                    </button>
                    <button
                      type="button"
                      disabled={isUploading}
                      onClick={() => handleUploadFlowActivity(true)}
                      className="art-btn-primary px-4 py-2 rounded-xl font-semibold disabled:opacity-50"
                    >
                      {isUploading ? 'Uploading…' : 'Yes, share'}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowShareConfirmation(false)}
                    className="mt-3 w-full text-sm text-gray-500 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Success popup after upload */}
            {showSuccessPopup && uploadSuccessData && (
              <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 border-2 border-amber-200">
                  <h3 className="text-xl font-bold text-amber-900 mb-2">Upload successful!</h3>
                  <p className="text-gray-700 text-sm mb-6">
                    {uploadSuccessData.shared
                      ? `Your artwork for "${uploadSuccessData.artworkTitle}" has been shared to the Art History Learning Co-Learning Space.`
                      : `Your artwork for "${uploadSuccessData.artworkTitle}" has been uploaded.`}
                  </p>
                  <div className="flex gap-3 justify-end">
                    {uploadSuccessData.shared ? (
                      <button
                        type="button"
                        onClick={() => {
                          setShowSuccessPopup(false);
                          navigate('/gallery/studio', { state: { activeTab: 'art-history' } });
                        }}
                        className="art-btn-primary px-4 py-2 rounded-xl font-semibold"
                      >
                        View in Co-Learning Space
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowSuccessPopup(false)}
                        className="art-btn-primary px-4 py-2 rounded-xl font-semibold"
                      >
                        OK
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
          </main>
        </>
      )}

      {/* Let's Go pop-up modal — shown after "Mark as Done" on Step 1 or Step 2 */}
      {showLetsGoModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border-2 border-amber-300 bg-white shadow-2xl p-6 md:p-8 relative">
            {/* Close button (X) in top right */}
            <button
              type="button"
              onClick={() => setShowLetsGoModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 rounded-full p-1 transition-colors"
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <p className="text-gray-900 font-semibold mb-6 text-center leading-relaxed">
              {letsGoModalStep === 1 && (
                <>
                  Now that you&apos;ve understood how this artwork was made, let&apos;s compare it to other artworks to learn how {effectiveArtworkTypeLabel || 'this type'} can be made.
                </>
              )}
              {letsGoModalStep === 2 && (
                <>
                  You&apos;ve compared techniques and style. Ready for a fun activity combining both artworks?
                </>
              )}
            </p>
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => {
                  if (letsGoModalStep === 1) handleGoToStep2();
                  else {
                    setShowLetsGoModal(false);
                    handleStartStep3();
                  }
                }}
                disabled={letsGoModalStep === 2 && step3Loading}
                className="py-3 px-8 rounded-xl text-base font-bold text-white focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg border-2 border-amber-700/40"
                style={{
                  background: 'linear-gradient(180deg, #D97706 0%, #B45309 50%, #92400E 100%)',
                  boxShadow: '0 4px 14px rgba(180, 83, 9, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                }}
              >
                {letsGoModalStep === 2 && step3Loading ? 'Creating…' : "Let's Go"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LearnArtHistoryFlow;
